// Asset name mappings between exchanges
// Maps from exchange-specific names to Aevo names.
//
// Every per-asset view aggregates the venue legs by the AEVO name, so a venue
// that spells the same instrument differently must be listed here or the
// instrument splits into two rows that never net — e.g. Aevo `WTIOIL` on the
// quote leg and Binance `CL` on the hedge leg. Source of truth is the desk's
// own mapping (aevo-mm/config/name_mappings.py MAPPINGS, mirrored by each
// asset's `hedging_asset` in aevo-mm/config/config_perp_hedger.py).

interface ExchangeMapping {
  aevo: string;
  binance?: string;
  bybit?: string;
  hyperliquid?: string;
  lighter?: string;
}

const ASSET_MAPPINGS: ExchangeMapping[] = [
  {
    aevo: "JITO",
    binance: "JTO",
    bybit: "JTO",
    hyperliquid: "JTO",
    lighter: "JTO",
  },
  {
    aevo: "TKO",
    bybit: "TAIKO",
  },
  {
    aevo: "PARCL",
    bybit: "PRCL",
  },
  {
    aevo: "BITCOIN",
    bybit: "HPOS10I",
    hyperliquid: "HPOS",
  },
  {
    aevo: "RAY",
    bybit: "RAYDIUM",
  },
  {
    aevo: "1000BONK",
    hyperliquid: "kBONK",
  },
  {
    aevo: "1000PEPE",
    hyperliquid: "kPEPE",
  },
  {
    aevo: "WTIOIL",
    binance: "CL",
    bybit: "CL",
    hyperliquid: "CL",
    lighter: "WTI",
  },
  {
    aevo: "PLATINUM",
    binance: "XPT",
    lighter: "XPT",
  },
  {
    aevo: "SKHX",
    binance: "SKHYNIX",
    bybit: "SKHYNIX",
    lighter: "SKHYNIX",
  },
  {
    aevo: "SMSN",
    binance: "SAMSUNG",
    bybit: "SAMSUNG",
    lighter: "SAMSUNG",
  },
  {
    aevo: "COPPER",
    lighter: "XCU",
  },
];

/** Venues whose symbols may differ from the Aevo asset name. */
export type HedgeExchange = "binance" | "bybit" | "hyperliquid" | "lighter";

/**
 * Convert an exchange-specific asset name to Aevo's naming convention
 */
export function convertToAevoAssetName(
  asset: string,
  exchange: HedgeExchange
): string {
  const mapping = ASSET_MAPPINGS.find((m) => m[exchange] === asset);
  return mapping ? mapping.aevo : asset;
}

/**
 * Convert from Aevo asset name to exchange-specific name
 */
export function convertFromAevoAssetName(
  aevoAsset: string,
  exchange: HedgeExchange
): string {
  const mapping = ASSET_MAPPINGS.find((m) => m.aevo === aevoAsset);
  return mapping && mapping[exchange] ? mapping[exchange]! : aevoAsset;
}
